import { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { formatCurrency, API_URL } from "../../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Search, Crown, CheckCircle2, PauseCircle, Clock, Users, ChevronDown, ChevronUp, Ticket, AlertTriangle, Sparkles, FileText, Ban, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const STATUS_BADGES = {
  active: { label: "Ativa", cls: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  trial: { label: "Trial", cls: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  expired: { label: "Expirada", cls: "bg-red-500/20 text-red-400 border-red-500/30" },
  suspended: { label: "Suspensa", cls: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30" },
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-PT") : "—");

export const OverviewTab = ({ headers, onGenerateKey }) => {
  const [stats, setStats] = useState(null);
  const [attention, setAttention] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});
  const [cancelCode, setCancelCode] = useState(null);

  const fetchAll = useCallback(async (q = "") => {
    try {
      const [sRes, aRes, cRes] = await Promise.all([
        axios.get(`${API_URL}/backoffice/stats`, { headers }),
        axios.get(`${API_URL}/backoffice/attention`, { headers }),
        axios.get(`${API_URL}/backoffice/companies${q ? `?search=${encodeURIComponent(q)}` : ""}`, { headers }),
      ]);
      setStats(sRes.data);
      setAttention(aRes.data);
      setCompanies(cRes.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao carregar dados"); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleDetail = async (cid) => {
    if (expanded === cid) { setExpanded(null); return; }
    setExpanded(cid);
    if (!details[cid]) {
      try {
        const res = await axios.get(`${API_URL}/backoffice/companies/${cid}/detail`, { headers });
        setDetails((d) => ({ ...d, [cid]: res.data }));
      } catch { toast.error("Erro ao carregar detalhe"); }
    }
  };

  const updateSub = async (companyId, payload, msg) => {
    setBusy(companyId);
    try {
      await axios.put(`${API_URL}/backoffice/companies/${companyId}/subscription`, payload, { headers });
      toast.success(msg);
      setDetails((d) => { const n = { ...d }; delete n[companyId]; return n; });
      await fetchAll(search);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao atualizar"); }
    finally { setBusy(""); }
  };

  const generateCancelCode = async (c) => {
    setBusy(c.company_id);
    try {
      const res = await axios.post(`${API_URL}/backoffice/companies/${c.company_id}/cancel-code`, {}, { headers });
      setCancelCode({ ...res.data, company_name: c.name });
      toast.success(`Código de anulação gerado para ${c.name}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao gerar código"); }
    finally { setBusy(""); }
  };

  const statCards = stats ? [
    { label: "Empresas", value: stats.total, icon: Building2, cls: "text-cyan-400" },
    { label: "Subscrições Ativas", value: stats.active, icon: CheckCircle2, cls: "text-emerald-400" },
    { label: "Em Trial", value: stats.trial, icon: Clock, cls: "text-amber-400" },
    { label: "Expiradas/Suspensas", value: (stats.expired || 0) + (stats.suspended || 0), icon: PauseCircle, cls: "text-red-400" },
    { label: "Receita Mensal (ativas)", value: formatCurrency(stats.mrr), icon: Crown, cls: "text-cyan-300" },
  ] : [];

  const attentionBlocks = attention ? [
    { key: "trials_expiring", title: "Trials a expirar (≤3 dias)", icon: AlertTriangle, cls: "text-amber-400 border-amber-500/30", items: attention.trials_expiring, fmt: (i) => `${i.days_left}d restantes` },
    { key: "subscriptions_ending", title: "Subscrições a acabar (≤7 dias)", icon: Clock, cls: "text-orange-400 border-orange-500/30", items: attention.subscriptions_ending, fmt: (i) => `${i.days_left}d restantes` },
    { key: "expired_or_suspended", title: "Expiradas / Suspensas", icon: PauseCircle, cls: "text-red-400 border-red-500/30", items: attention.expired_or_suspended, fmt: (i) => (i.status === "suspended" ? "suspensa" : "expirada") },
    { key: "new_companies", title: "Novos registos (7 dias)", icon: Sparkles, cls: "text-emerald-400 border-emerald-500/30", items: attention.new_companies, fmt: (i) => fmtDate(i.created_at) },
  ] : [];

  return (
    <div className="space-y-8">
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-5" data-testid="backoffice-stats">
        {statCards.map((s) => (
          <div key={s.label} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <s.icon className={`w-5 h-5 mb-2 ${s.cls}`} />
            <p className="text-2xl font-bold font-mono">{s.value}</p>
            <p className="text-xs text-white/40">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="backoffice-attention">
        {attentionBlocks.map((b) => (
          <div key={b.key} className={`rounded-2xl border bg-white/[0.02] p-4 ${b.cls.split(" ")[1]}`} data-testid={`attention-${b.key}`}>
            <div className="flex items-center gap-2 mb-3">
              <b.icon className={`w-4 h-4 ${b.cls.split(" ")[0]}`} />
              <p className="text-xs font-semibold text-white/70">{b.title}</p>
            </div>
            {b.items?.length ? (
              <ul className="space-y-1.5">
                {b.items.slice(0, 5).map((i) => (
                  <li key={i.company_id} className="flex items-center justify-between text-xs">
                    <span className="truncate">{i.name}</span>
                    <span className="text-white/40 shrink-0 ml-2">{b.fmt(i)}</span>
                  </li>
                ))}
                {b.items.length > 5 && <li className="text-[11px] text-white/30">+{b.items.length - 5} mais</li>}
              </ul>
            ) : (
              <p className="text-xs text-white/25">Nada por agora</p>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchAll(search)}
            placeholder="Pesquisar por nome ou NIF..."
            className="pl-9 bg-white/5 border-white/15 text-white placeholder:text-white/30"
            data-testid="backoffice-search-input"
          />
        </div>
        <Button variant="outline" className="border-white/15 bg-transparent text-white/70 hover:bg-white/10 hover:text-white" onClick={() => fetchAll(search)} data-testid="backoffice-search-btn">
          Pesquisar
        </Button>
      </div>

      <div className="space-y-3">
        {companies.map((c) => {
          const badge = STATUS_BADGES[c.plan_status] || STATUS_BADGES.active;
          const isBusy = busy === c.company_id;
          const det = details[c.company_id];
          return (
            <div key={c.company_id} className="rounded-2xl border border-white/10 bg-white/[0.03]" data-testid={`company-row-${c.company_id}`}>
              <div className="p-4 flex flex-wrap items-center gap-4">
                <button onClick={() => toggleDetail(c.company_id)} className="text-white/40 hover:text-white" data-testid={`expand-company-${c.company_id}`}>
                  {expanded === c.company_id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div className="min-w-[200px] flex-1">
                  <p className="font-bold">{c.name}</p>
                  <p className="text-xs text-white/40">
                    NIF {c.nif || "—"} · {c.owner_email || "sem dono"} · <Users className="inline w-3 h-3" /> {c.users_count}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={c.plan === "ouro" ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/30" : "bg-amber-600/20 text-amber-500 border-amber-600/30"}>
                    {c.plan === "ouro" ? "Ouro" : "Bronze"} · {c.billing_cycle === "annual" ? "Anual" : "Mensal"}
                  </Badge>
                  <Badge className={badge.cls} data-testid={`company-status-${c.company_id}`}>
                    {badge.label}{c.plan_status === "trial" && c.trial_days_left !== null ? ` (${c.trial_days_left}d)` : ""}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 flex-wrap ml-auto">
                  <Button size="sm" className="bg-cyan-500/15 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/30" disabled={isBusy} onClick={() => onGenerateKey({ company_id: c.company_id, name: c.name })} data-testid={`generate-key-btn-${c.company_id}`}>
                    <Ticket className="w-3.5 h-3.5 mr-1" /> Chave
                  </Button>
                  <Button size="sm" className="bg-red-500/15 hover:bg-red-500/30 text-red-300 border border-red-500/30" disabled={isBusy} onClick={() => generateCancelCode(c)} title="Gerar código de anulação de fatura para esta empresa" data-testid={`cancel-code-btn-${c.company_id}`}>
                    <Ban className="w-3.5 h-3.5 mr-1" /> Cód. Anulação
                  </Button>
                  <Select
                    value={c.plan}
                    onValueChange={(v) => v !== c.plan && updateSub(c.company_id, { plan: v }, `Plano de ${c.name} mudado para ${v === "ouro" ? "Ouro" : "Bronze"}`)}
                    disabled={isBusy}
                  >
                    <SelectTrigger className="w-[105px] h-8 text-xs bg-white/5 border-white/15 text-white" data-testid={`plan-select-${c.company_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bronze">Bronze</SelectItem>
                      <SelectItem value="ouro">Ouro</SelectItem>
                    </SelectContent>
                  </Select>
                  {c.plan_status !== "suspended" ? (
                    <Button size="sm" variant="outline" disabled={isBusy} className="border-white/15 bg-transparent text-white/70 hover:bg-white/10 hover:text-white" onClick={() => updateSub(c.company_id, { status: "suspended" }, `Subscrição de ${c.name} suspensa`)} data-testid={`suspend-company-btn-${c.company_id}`}>
                      Suspender
                    </Button>
                  ) : (
                    <Button size="sm" disabled={isBusy} className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold" onClick={() => updateSub(c.company_id, { status: "active" }, `Subscrição de ${c.name} reativada`)} data-testid={`activate-company-btn-${c.company_id}`}>
                      Reativar
                    </Button>
                  )}
                  <Button size="sm" variant="outline" disabled={isBusy} className="border-white/15 bg-transparent text-white/70 hover:bg-white/10 hover:text-white" onClick={() => updateSub(c.company_id, { extend_trial_days: 14 }, `Trial de ${c.name} prolongado por 14 dias`)} data-testid={`extend-trial-btn-${c.company_id}`}>
                    +14d
                  </Button>
                </div>
              </div>

              {expanded === c.company_id && (
                <div className="border-t border-white/10 p-4" data-testid={`company-detail-${c.company_id}`}>
                  {!det ? (
                    <p className="text-sm text-white/40">A carregar detalhe...</p>
                  ) : (
                    <div className="grid gap-5 lg:grid-cols-3">
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-white/50 uppercase tracking-wide flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Faturação</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="rounded-lg bg-white/5 p-2.5">
                            <p className="text-lg font-bold font-mono">{formatCurrency(det.revenue_total)}</p>
                            <p className="text-[11px] text-white/40">Total ({det.invoices_count} faturas)</p>
                          </div>
                          <div className="rounded-lg bg-white/5 p-2.5">
                            <p className="text-lg font-bold font-mono">{formatCurrency(det.revenue_30d)}</p>
                            <p className="text-[11px] text-white/40">Últimos 30 dias ({det.invoices_30d})</p>
                          </div>
                        </div>
                        <p className="text-xs text-white/40">Última fatura: {fmtDate(det.last_invoice_at)} · Última atividade: {fmtDate(det.last_activity_at)}</p>
                        <p className="text-xs text-white/40">Registada em: {fmtDate(det.created_at)}{det.subscription_ends_at ? ` · Subscrição até: ${fmtDate(det.subscription_ends_at)}` : ""}</p>
                        {det.custom_domain && (
                          <p className="text-xs text-white/60" data-testid={`company-domain-${c.company_id}`}>
                            Domínio próprio: <span className="font-mono text-cyan-300">{det.custom_domain}</span>{" "}
                            <Badge className={`text-[10px] ${det.custom_domain_verified_at ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{det.custom_domain_verified_at ? "verificado" : "pendente — activar na plataforma"}</Badge>
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-white/50 uppercase tracking-wide flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Utilizadores ({det.users.length})</p>
                        <ul className="space-y-1 max-h-36 overflow-y-auto pr-1">
                          {det.users.map((u) => (
                            <li key={u.email} className="flex items-center justify-between text-xs bg-white/5 rounded px-2 py-1.5">
                              <span className="truncate">{u.name} <span className="text-white/40">· {u.email}</span></span>
                              <Badge className="bg-white/10 text-white/60 text-[10px] ml-2 shrink-0">{u.role}</Badge>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-white/50 uppercase tracking-wide flex items-center gap-1.5"><Ticket className="w-3.5 h-3.5" /> Chaves ({det.keys.length})</p>
                        {det.keys.length ? (
                          <ul className="space-y-1 max-h-36 overflow-y-auto pr-1">
                            {det.keys.map((k) => (
                              <li key={k.key_id} className="flex items-center justify-between text-xs bg-white/5 rounded px-2 py-1.5">
                                <span className="font-mono truncate">{k.code}</span>
                                <span className="text-white/40 shrink-0 ml-2">{k.status === "used" ? `usada ${fmtDate(k.used_at)}` : k.status === "revoked" ? "revogada" : "pendente"}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-xs text-white/25">Sem chaves emitidas</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {companies.length === 0 && <p className="text-center text-white/40 py-10">Nenhuma empresa encontrada</p>}
      </div>

      <Dialog open={!!cancelCode} onOpenChange={(o) => !o && setCancelCode(null)}>
        <DialogContent className="max-w-sm bg-[#101418] border-white/10 text-white" data-testid="backoffice-cancel-code-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Ban className="w-5 h-5 text-red-400" /> Código de Anulação</DialogTitle>
            <DialogDescription className="text-white/50">
              Para <strong className="text-white">{cancelCode?.company_name}</strong>. Uso único, válido 24h. Entregue ao administrador/operador da empresa. A geração ficou registada na auditoria da empresa.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-5 text-center space-y-2">
            <p className="font-mono text-4xl font-black tracking-[0.35em]" data-testid="backoffice-cancel-code-value">{cancelCode?.code}</p>
            <p className="text-xs text-white/40">Válido até {cancelCode ? new Date(cancelCode.expires_at).toLocaleString("pt-PT") : ""}</p>
            <Button size="sm" variant="outline" className="border-white/15 bg-transparent text-white/80 hover:bg-white/10" onClick={() => navigator.clipboard?.writeText(cancelCode?.code || "").then(() => toast.success("Código copiado")).catch(() => {})} data-testid="backoffice-copy-cancel-code-btn">
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copiar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
